import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, ConfigProvider, Segmented } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { ContentCustom, PageHeaderCustom } from '../../components';
import { routes } from '../../utils/constants/adminRoutes';
import { useErrorMessage } from '../../utils/errorMessage';
import { useAuthContext } from '../../contexts/AuthContext';
import { useSocketContext } from '../../contexts/SocketContext';
import { useTabsItems } from './tabs/tabsItems';
import { useCallsAPI } from './utils/callsAPI';
import { dashboardTheme } from './utils/dashboardTheme';
import {
  getDisplayableNotifications,
  isUnreadNotification
} from './utils/alertColumns';

const ALERTS_TAB = 1;

const StyledToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const StyledToolbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const StyledTabBadge = styled.span`
  margin-left: 8px;
  padding: 1px 7px;
  border-radius: var(--ds-radius-md);
  font-size: 11px;
  font-weight: 700;
  background: var(--pear);
  color: var(--darkGray);
`;

const StyledMarkAllRead = styled.button`
  padding: 0;
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: var(--ds-cambridge-dark);
  cursor: pointer;

  &:hover {
    color: var(--darkGray);
  }
`;

// Specificity is raised to win against the global primary button style.
const StyledCreateButton = styled(Button)`
  && {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 18px;
    border: none;
    border-radius: var(--ds-radius-md);
    font-size: 14px;
    font-weight: 600;
    background: var(--pear);
    color: var(--darkGray);
  }

  &&:hover {
    background: var(--pear);
    color: var(--darkGray);
    filter: brightness(0.95);
  }
`;

/**
 * Dashboard component for displaying user data and notifications.
 *
 * @component
 * @returns {React.ReactNode} - The rendered component.
 */
export const Dashboard = () => {
  const [activeTab, setActiveTab] = useState(ALERTS_TAB);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [supervisions, setSupervisions] = useState([]);
  const [supervisionsStatus, setSupervisionsStatus] = useState();
  const [customerInvoices, setCustomerInvoices] = useState({});
  const [internInvoices, setInternInvoices] = useState({});
  const [registrations, setRegistrations] = useState({});
  const [subscriptions, setSubscriptions] = useState({});

  const [statusDate, setStatusDate] = useState(null);
  const [supDate, setSupDate] = useState(null);
  const [financeDate, setFinanceDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [statisticsDate, setStatisticsDate] = useState(
    dayjs().format('YYYY-MM-DD')
  );

  const { t } = useTranslation();
  const { dispatchAPI, user } = useAuthContext();
  const { message } = useErrorMessage();
  const { socket } = useSocketContext();
  const navigate = useNavigate();

  // hooks for get all statistics data
  const {
    getNotifications,
    getSupervisionsData,
    getCustomerInvoicesData,
    getInternInvoicesData,
    getRegistrationsData,
    getSupervisionsStatus,
    getSubscriptions
  } = useCallsAPI({
    setSupervisions,
    setCustomerInvoices,
    setNotifications,
    setInternInvoices,
    setRegistrations,
    setLoading,
    setSupervisionsStatus,
    statusDate,
    supDate,
    financeDate,
    statisticsDate,
    setSubscriptions
  });

  // tabs array for the dashboard
  const { tabsItems } = useTabsItems({
    notifications,
    t,
    forceRefresh,
    setForceRefresh,
    message,
    dispatchAPI,
    user,
    loading,
    supervisions,
    customerInvoices,
    internInvoices,
    registrations,
    supervisionsStatus,
    setStatusDate,
    setSupDate,
    setFinanceDate,
    setStatisticsDate,
    subscriptions,
    statisticsDate
  });

  const unreadNotifications = getDisplayableNotifications(notifications).filter(
    (notification) => isUnreadNotification(notification, user?._id)
  );

  const markAllAsRead = async () => {
    try {
      await Promise.all(
        unreadNotifications.map(({ _id }) =>
          dispatchAPI('PATCH', { url: `notifications/${_id}` })
        )
      );
      await getNotifications();
    } catch (e) {
      message(e);
    }
  };

  const tabsOptions = tabsItems.map(({ key, label }) => ({
    value: key,
    label:
      key === ALERTS_TAB && unreadNotifications.length > 0 ? (
        <>
          {label}
          <StyledTabBadge>{unreadNotifications.length}</StyledTabBadge>
        </>
      ) : (
        label
      )
  }));

  useEffect(() => {
    (async () => {
      await getNotifications();
    })();
  }, []);

  useEffect(() => {
    (async () => {
      await getSupervisionsStatus();
    })();
  }, [statusDate]);

  useEffect(() => {
    (async () => {
      await getSupervisionsData();
    })();
  }, [supDate]);

  useEffect(() => {
    (async () => {
      await getCustomerInvoicesData();
      await getInternInvoicesData();
    })();
  }, [financeDate]);

  useEffect(() => {
    (async () => {
      await Promise.all([getRegistrationsData(), getSubscriptions()]);
    })();
  }, [statisticsDate]);

  // socket listeners for notifications
  useEffect(() => {
    if (socket) {
      const handleNewNotification = () => getNotifications();
      const handleDeleteNotification = () => getNotifications();
      const handleUpdateNotification = () => getNotifications();

      socket.on('newNotification', handleNewNotification);
      socket.on('deleteNotification', handleDeleteNotification);
      socket.on('updateNotification', handleUpdateNotification);

      return () => {
        socket.off('newNotification', handleNewNotification);
        socket.off('deleteNotification', handleDeleteNotification);
        socket.off('updateNotification', handleUpdateNotification);
      };
    }
    return undefined;
  }, [socket]);

  return (
    <>
      <PageHeaderCustom
        title={t('basiclayout.menu.dashboard')}
        subTitle={dayjs().format('dddd D MMMM YYYY')}
        withoutBackIcon
        withoutBreadCrumb
      />
      <ContentCustom
        style={{
          background: 'transparent',
          padding: '8px 40px 32px',
          margin: 0,
          gap: 24
        }}
      >
        <ConfigProvider theme={dashboardTheme}>
          <StyledToolbar>
            <Segmented
              options={tabsOptions}
              value={activeTab}
              onChange={setActiveTab}
            />
            <StyledToolbarActions>
              {activeTab === ALERTS_TAB && unreadNotifications.length > 0 && (
                <StyledMarkAllRead type="button" onClick={markAllAsRead}>
                  {t('notifications.mark_all_as_read')}
                </StyledMarkAllRead>
              )}
              <StyledCreateButton
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate(`${routes.SUPERVISIONS}/create`)}
              >
                {t('owners.buttons.create_supervision')}
              </StyledCreateButton>
            </StyledToolbarActions>
          </StyledToolbar>
          {tabsItems.find(({ key }) => key === activeTab)?.children}
        </ConfigProvider>
      </ContentCustom>
    </>
  );
};
